import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';

const ANIMATION_OPTIONS = {
  duration: 500,
};

/**
 * A custom element that displays a marquee.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} wrapper - The wrapper element.
 * @property {HTMLElement} content - The content element.
 * @property {HTMLElement[]} marqueeItems - The marquee items collection.
 *
 * @extends Component<Refs>
 */
class MarqueeComponent extends Component {
  requiredRefs = ['wrapper', 'content', 'marqueeItems'];

  #resizeObserver = null;

  connectedCallback() {
    super.connectedCallback();

    const { marqueeItems } = this.refs;
    if (marqueeItems.length === 0) return;

    this.#insertBullets();
    this.#setupMarquee();

    if (typeof ResizeObserver !== 'undefined') {
      this.#resizeObserver = new ResizeObserver(() => {
        if (!this.hasAttribute('data-ready')) {
          this.#setupMarquee();
        } else {
          this.#handleResize();
        }
      });
      this.#resizeObserver.observe(this);
    } else {
      window.addEventListener('resize', this.#handleResize);
    }

    if (document.fonts) {
      document.fonts.ready.then(() => {
        this.#setupMarquee();
      });
    }

    this.addEventListener('pointerenter', this.#slowDown);
    this.addEventListener('pointerleave', this.#speedUp);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.#resizeObserver) {
      this.#resizeObserver.disconnect();
    } else {
      window.removeEventListener('resize', this.#handleResize);
    }
    this.#handleResize.cancel();
    this.removeEventListener('pointerenter', this.#slowDown);
    this.removeEventListener('pointerleave', this.#speedUp);
  }

  updatedCallback() {
    super.updatedCallback();
    this.#insertBullets();
    this.#setupMarquee();
  }

  /**
   * @type {{ cancel: () => void, current: number } | null}
   */
  #animation = null;

  #slowDown = debounce(() => {
    if (this.#animation) return;

    const animation = this.refs.wrapper.getAnimations()[0];

    if (!animation) return;

    this.#animation = animateValue({
      ...ANIMATION_OPTIONS,
      from: 1,
      to: 0,
      onUpdate: (value) => animation.updatePlaybackRate(value),
      onComplete: () => {
        this.#animation = null;
      },
    });
  }, ANIMATION_OPTIONS.duration);

  #speedUp() {
    this.#slowDown.cancel();

    const animation = this.refs.wrapper.getAnimations()[0];

    if (!animation || animation.playbackRate === 1) return;

    const from = this.#animation?.current ?? 0;
    this.#animation?.cancel();

    this.#animation = animateValue({
      ...ANIMATION_OPTIONS,
      from,
      to: 1,
      onUpdate: (value) => animation.updatePlaybackRate(value),
      onComplete: () => {
        this.#animation = null;
      },
    });
  }

  get clonedContent() {
    const { content, wrapper } = this.refs;
    const lastChild = wrapper.lastElementChild;

    return content !== lastChild ? lastChild : null;
  }

  /**
   * @param {number} value
   */
  #setSpeed(value) {
    this.style.setProperty('--marquee-speed', `${value}s`);
  }

  #calculateSpeed(numberOfCopies) {
    const speedFactor = Number(this.getAttribute('data-speed-factor'));
    const speed = Math.sqrt(numberOfCopies) * speedFactor;

    return speed;
  }

  #handleResize = debounce(() => {
    this.#setupMarquee();
  }, 250);

  #setupMarquee() {
    const { marqueeItems } = this.refs;
    if (!marqueeItems || marqueeItems.length === 0) return;

    // Get current dimensions
    const marqueeWidth = this.getBoundingClientRect().width;
    const marqueeItemsWidth = marqueeItems[0].getBoundingClientRect().width;

    // If container width is 0 or items width is 0, we can't calculate copies yet.
    if (marqueeWidth === 0 || marqueeItemsWidth === 0) return;

    // Reset to initial state before setup to ensure clean cloning
    this.#resetRepeatedItems();

    // Re-read marqueeItems[0] because references might have been updated by reset
    const firstItem = this.refs.marqueeItems[0];
    if (!firstItem) return;
    const firstItemWidth = firstItem.getBoundingClientRect().width;

    // Calculate number of copies needed to span the width of the marquee container
    const numberOfCopies = Math.ceil(marqueeWidth / firstItemWidth) + 1;
    const speed = this.#calculateSpeed(numberOfCopies);

    // Clone the first item (numberOfCopies - 1) times
    for (let i = 0; i < numberOfCopies - 1; i++) {
      const clone = firstItem.cloneNode(true);
      this.refs.content.appendChild(clone);
    }

    // Sync references
    super.updatedCallback();

    // Duplicate content for infinite scrolling
    this.#duplicateContent();
    this.#setSpeed(speed);
    
    if (!this.hasAttribute('data-ready')) {
      this.setAttribute('data-ready', 'true');
    }
    
    this.#restartAnimation();
  }

  #resetRepeatedItems() {
    const { content } = this.refs;
    if (!content) return;

    this.clonedContent?.remove();

    const items = Array.from(content.children);
    // Keep only the first element (which is marqueeItems[0])
    for (let i = 1; i < items.length; i++) {
      items[i]?.remove();
    }

    super.updatedCallback();
  }

  #restartAnimation() {
    const animations = this.refs.wrapper.getAnimations();

    requestAnimationFrame(() => {
      for (const animation of animations) {
        animation.currentTime = 0;
      }
    });
  }

  #duplicateContent() {
    this.clonedContent?.remove();

    const clone = /** @type {HTMLElement} */ (this.refs.content.cloneNode(true));

    clone.setAttribute('aria-hidden', 'true');
    clone.removeAttribute('ref');

    this.refs.wrapper.appendChild(clone);
  }

  #insertBullets() {
    const { marqueeItems } = this.refs;
    if (marqueeItems.length === 0) return;

    const container = marqueeItems[0];
    const children = Array.from(container.children).filter(
      (child) => !child.classList.contains('marquee__bullet')
    );

    if (children.length === 0) return;

    // Clear existing bullets
    container.querySelectorAll('.marquee__bullet').forEach((el) => el.remove());

    // Insert a bullet before every child except the first
    for (let i = 1; i < children.length; i++) {
      const bullet = document.createElement('span');
      bullet.className = 'marquee__bullet';
      bullet.textContent = '•';
      bullet.style.fontSize = '1.2em';
      bullet.style.lineHeight = '1';
      bullet.style.display = 'inline-flex';
      bullet.style.alignItems = 'center';
      bullet.style.justifyContent = 'center';
      bullet.style.color = 'inherit';
      container.insertBefore(bullet, children[i]);
    }

    // Append a trailing bullet at the end of the items
    const trailingBullet = document.createElement('span');
    trailingBullet.className = 'marquee__bullet';
    trailingBullet.textContent = '•';
    trailingBullet.style.fontSize = '1.2em';
    trailingBullet.style.lineHeight = '1';
    trailingBullet.style.display = 'inline-flex';
    trailingBullet.style.alignItems = 'center';
    trailingBullet.style.justifyContent = 'center';
    trailingBullet.style.color = 'inherit';
    container.appendChild(trailingBullet);
  }
}

// Define the animateValue function
/**
 * Animate a numeric property smoothly.
 * @param {Object} params - The parameters for the animation.
 * @param {number} params.from - The starting value.
 * @param {number} params.to - The ending value.
 * @param {number} params.duration - The duration of the animation in milliseconds.
 * @param {function(number): void} params.onUpdate - The function to call on each update.
 * @param {function(number): number} [params.easing] - The easing function.
 * @param {function(): void} [params.onComplete] - The function to call when the animation completes.
 */
function animateValue({ from, to, duration, onUpdate, easing = (t) => t * t * (3 - 2 * t), onComplete }) {
  const startTime = performance.now();
  let cancelled = false;
  let currentValue = from;

  /**
   * @param {number} currentTime - The current time in milliseconds.
   */
  function animate(currentTime) {
    if (cancelled) return;

    const elapsed = currentTime - startTime;
    const progress = Math.min(elapsed / duration, 1);
    const easedProgress = easing(progress);
    currentValue = from + (to - from) * easedProgress;

    onUpdate(currentValue);

    if (progress < 1) {
      requestAnimationFrame(animate);
    } else if (typeof onComplete === 'function') {
      onComplete();
    }
  }

  requestAnimationFrame(animate);

  return {
    get current() {
      return currentValue;
    },
    cancel() {
      cancelled = true;
    },
  };
}

if (!customElements.get('marquee-component')) {
  customElements.define('marquee-component', MarqueeComponent);
}
